import {
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
    TextField,
    IconButton,
    Typography,
    Button,
    Pagination,
    InputAdornment,
    CardActionArea,
    useMediaQuery,
    CircularProgress
} from "@mui/material";
import {Contacts, Delete, Search, ArrowUpward, ArrowDownward, Close} from "@mui/icons-material";
import {useState, useCallback, useEffect, useMemo, useRef} from "react";
import axios from "axios";
import {AuthContext} from "../context/auth.context";
import {useContext} from "react";

interface Contact {
    id: string;
    email: string;
    fullName: string;
    phone?: string;
}

interface ContactsResponse {
    contacts: Contact[];
    totalPages: number;
    totalElements: number;
}

interface AddressBookProps {
    open: boolean;
    onClose: () => void;
    onSelect: (email: string) => void;
    fieldType: 'to' | 'copyTo';
    existingEmails?: string[];
}

export const AddressBook = ({open, onClose, onSelect, fieldType, existingEmails = []}: AddressBookProps) => {
    const {user} = useContext(AuthContext);
    const isMobile = useMediaQuery('(max-width:500px)');
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [domainContacts, setDomainContacts] = useState<Contact[]>([]);
    const [page, setPage] = useState(1);
    const [domainPage, setDomainPage] = useState(1);
    const [queryString, setQueryString] = useState("");
    const [domainQueryString, setDomainQueryString] = useState("");
    const [sort, setSort] = useState<"asc" | "desc">("asc");
    const [domainSort, setDomainSort] = useState<"asc" | "desc">("asc");
    const [totalPages, setTotalPages] = useState(1);
    const [domainTotalPages, setDomainTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [domainLoading, setDomainLoading] = useState(false);
    const [showDomainSearch, setShowDomainSearch] = useState(false);
    const [queryInput, setQueryInput] = useState("");
    const [domainQueryInput, setDomainQueryInput] = useState("");
    const queryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const domainQueryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isDomainUser = useMemo(() => {
        return user?.external === "false";
    }, [user]);

    const isExternalUser = useMemo(() => {
        return user?.scope?.split(' ')?.includes('external');
    }, [user]);

    const fetchContacts = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({
            page: String(page),
            pageSize: "10",
            direction: sort
        });
        if (queryString) {
            params.append("queryString", queryString);
        }
        axios.get(`/eus/addressbook/user/contact?${params.toString()}`)
            .then(resp => {
                const data = resp.data;
                const contactsList = Array.isArray(data) ? data : (data?.contacts || []);
                setContacts(contactsList);
                const totalPagesHeader = resp.headers['x-total-pages'] || resp.headers['X-Total-Pages'];
                if (totalPagesHeader) {
                    setTotalPages(parseInt(String(totalPagesHeader)));
                } else {
                    setTotalPages(1);
                }
            })
            .catch(() => {
                setContacts([]);
                setTotalPages(1);
            })
            .finally(() => setLoading(false));
    }, [page, queryString, sort]);

    const fetchDomainContacts = useCallback(() => {
        if (isExternalUser) {
            setDomainContacts([]);
            setDomainTotalPages(1);
            return;
        }
        setDomainLoading(true);
        const params = new URLSearchParams({
            page: String(domainPage),
            pageSize: "10",
            direction: domainSort
        });
        if (domainQueryString) {
            params.append("queryString", domainQueryString);
        }
        axios.get(`/eus/addressbook/domain?${params.toString()}`)
            .then(resp => {
                const data = resp.data;
                const contactsList = Array.isArray(data) ? data : (data?.contacts || []);
                setDomainContacts(contactsList);
                const totalPagesHeader = resp.headers['x-total-pages'] || resp.headers['X-Total-Pages'];
                if (totalPagesHeader) {
                    setDomainTotalPages(parseInt(String(totalPagesHeader)));
                } else {
                    setDomainTotalPages(1);
                }
            })
            .catch((err) => {
                if (err.response?.status === 403) {
                    setDomainContacts([]);
                    setDomainTotalPages(1);
                } else {
                    setDomainContacts([]);
                    setDomainTotalPages(1);
                }
            })
            .finally(() => setDomainLoading(false));
    }, [domainPage, domainQueryString, domainSort, isExternalUser]);

    useEffect(() => {
        if (open) {
            setQueryInput("");
            setQueryString("");
            setPage(1);
            setSort("asc");
            setShowDomainSearch(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && !showDomainSearch) {
            fetchContacts();
        }
    }, [open, showDomainSearch, page, queryString, sort, fetchContacts]);

    useEffect(() => {
        if (open && showDomainSearch) {
            fetchDomainContacts();
        }
    }, [open, showDomainSearch, domainPage, domainQueryString, domainSort, fetchDomainContacts]);

    useEffect(() => {
        if (open && !showDomainSearch) {
            if (queryTimeoutRef.current) {
                clearTimeout(queryTimeoutRef.current);
            }
            queryTimeoutRef.current = setTimeout(() => {
                setQueryString(queryInput);
                setPage(1);
            }, 1000);
            return () => {
                if (queryTimeoutRef.current) {
                    clearTimeout(queryTimeoutRef.current);
                }
            };
        }
    }, [open, showDomainSearch, queryInput]);

    useEffect(() => {
        if (open && showDomainSearch) {
            if (domainQueryTimeoutRef.current) {
                clearTimeout(domainQueryTimeoutRef.current);
            }
            domainQueryTimeoutRef.current = setTimeout(() => {
                setDomainQueryString(domainQueryInput);
                setDomainPage(1);
            }, 1000);
            return () => {
                if (domainQueryTimeoutRef.current) {
                    clearTimeout(domainQueryTimeoutRef.current);
                }
            };
        }
    }, [open, showDomainSearch, domainQueryInput]);

    const handleDelete = useCallback((id: string) => {
        axios.delete(`/eus/addressbook/user/contact/${id}`)
            .then(() => {
                fetchContacts();
            })
            .catch(() => {});
    }, [fetchContacts]);

    const handleSelect = useCallback((email: string) => {
        onSelect(email);
        onClose();
    }, [onSelect, onClose]);

    const toggleSort = useCallback(() => {
        setSort(prev => prev === "asc" ? "desc" : "asc");
        setPage(1);
    }, []);

    const toggleDomainSort = useCallback(() => {
        setDomainSort(prev => prev === "asc" ? "desc" : "asc");
        setDomainPage(1);
    }, []);

    const currentContacts = showDomainSearch ? domainContacts : contacts;
    const currentTotalPages = showDomainSearch ? domainTotalPages : totalPages;
    const currentPage = showDomainSearch ? domainPage : page;
    const currentSort = showDomainSearch ? domainSort : sort;
    const currentToggleSort = showDomainSearch ? toggleDomainSort : toggleSort;
    const currentQueryInput = showDomainSearch ? domainQueryInput : queryInput;
    const currentSetQueryInput = showDomainSearch ? setDomainQueryInput : setQueryInput;
    const currentSetPage = showDomainSearch ? setDomainPage : setPage;

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth 
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    minWidth: isMobile ? '100%' : '600px',
                    minHeight: isMobile ? '100%' : '500px',
                    maxHeight: isMobile ? '100%' : '80vh'
                }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>Адресная книга</Typography>
                    {isMobile && (
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ 
                display: "flex", 
                flexDirection: "column", 
                position: "relative", 
                minHeight: isMobile ? "60vh" : "400px",
                height: isMobile ? "calc(100vh - 120px)" : "600px",
                overflow: "hidden"
            }}>
                <Stack spacing={2} sx={{ mt: 1, flexShrink: 0 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            fullWidth={isMobile}
                            sx={isMobile ? {} : { flex: "1 1 auto", minWidth: 0 }}
                            placeholder="Поиск по ФИО/email/телефону"
                            value={currentQueryInput}
                            onChange={(e) => currentSetQueryInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: isMobile ? "100%" : "auto" }}>
                            <IconButton onClick={currentToggleSort} sx={{ flexShrink: 0 }}>
                                {currentSort === "asc" ? <ArrowUpward /> : <ArrowDownward />}
                            </IconButton>
                            {isDomainUser && !isExternalUser && !showDomainSearch && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ flexShrink: 0, whiteSpace: "nowrap", flex: isMobile ? 1 : "none" }}
                                    onClick={() => {
                                        setShowDomainSearch(true);
                                        setDomainQueryInput("");
                                        setDomainQueryString("");
                                        setDomainPage(1);
                                        setDomainSort("asc");
                                    }}
                                >
                                    Поиск по всем пользователям
                                </Button>
                            )}
                            {showDomainSearch && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ flexShrink: 0, flex: isMobile ? 1 : "none" }}
                                    onClick={() => {
                                        setShowDomainSearch(false);
                                    }}
                                >
                                    Мои контакты
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Stack>

                <Stack sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
                    {(loading || domainLoading) && (
                        <Stack 
                            alignItems="center" 
                            justifyContent="center" 
                            sx={{ 
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                zIndex: 1
                            }}
                        >
                            <CircularProgress />
                        </Stack>
                    )}
                    <Stack 
                        spacing={1} 
                        sx={{ 
                            opacity: (loading || domainLoading) ? 0.3 : 1,
                            height: "100%",
                            overflowY: "auto"
                        }}
                    >
                    {currentContacts.length === 0 && !loading && !domainLoading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                            <Typography>Нет доступных контактов</Typography>
                        </Stack>
                    ) : (
                        currentContacts.map((contact) => {
                            const isDisabled = existingEmails.some(existing => {
                                const existingEmailsList = existing.split(/[,;]/).map(e => e.trim().toLowerCase());
                                return existingEmailsList.includes(contact.email.toLowerCase());
                            });
                            return (
                            <CardActionArea
                                key={contact.id}
                                onClick={() => !isDisabled && handleSelect(contact.email)}
                                disabled={isDisabled || loading || domainLoading}
                                sx={{ borderRadius: 1, opacity: isDisabled ? 0.5 : 1 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="flex-start"
                                    sx={{ p: 1, position: "relative", minHeight: 60 }}
                                >
                                    <Stack flexGrow={1}>
                                        <Typography variant="body1">
                                            {contact.fullName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                                            {contact.email}
                                        </Typography>
                                        {contact.phone && (
                                            <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                                                {contact.phone}
                                            </Typography>
                                        )}
                                    </Stack>
                                    {!showDomainSearch && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(contact.id);
                                            }}
                                            sx={{ alignSelf: "flex-end", mt: "auto" }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                            </CardActionArea>
                            );
                        })
                    )}
                    </Stack>
                </Stack>

                {currentContacts.length > 0 && (
                    <Stack direction="row" justifyContent="center" sx={{ opacity: (loading || domainLoading) ? 0.3 : 1, flexShrink: 0, pt: 2 }}>
                        <Pagination
                            page={currentPage}
                            count={currentTotalPages}
                            onChange={(_, p) => currentSetPage(p)}
                            size="small"
                            disabled={loading || domainLoading}
                        />
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

